import {
  type PropsWithChildren,
  type JSX,
  useMemo,
  useEffect,
  createContext,
  useContext,
} from 'react';
import {
  type Theme,
  getLightTheme,
  paletteTokens,
  BrandingContext,
  type BrandingContextValue,
} from '@doxyme/ui/theme';
import { colord, extend } from 'colord';
import mixPlugin from 'colord/plugins/mix';
import { useGetClinicBrandingQuery } from '../services/branding/operations.generated';
import { useTenantId } from '../hooks/useAuth';
import { useAppDispatch } from '../hooks/useReduxToolkit';
import { initializeAsync as initializeBranding } from '../features/branding/branding.slice';
import { useUserEntitlementsContext } from './userEntitlementsContext';
import { FronteggFeatureKeys } from './userEntitlementsContext.types';

extend([mixPlugin]);

const lightTheme = getLightTheme() as Theme;

/** Fixed overrides of the theme, to apply along with any custom branding. */
const themeOverrideWhenCustom = {
  background: {
    default: {
      backend: lightTheme.background.default.backend,
    },
  },
};

const brandingToPalette = (
  primaryColor?: string | null,
): BrandingContextValue['overridePalette'] | undefined => {
  const primary =
    typeof primaryColor === 'string' ? colord(primaryColor) : undefined;

  if (!primary) {
    return undefined;
  }

  return {
    dynamicContrast: {
      primary: {
        100: lightTheme.palette.getContrastText(primary.toRgbString()),
      },
    },
    brand: {
      primary: {
        25: primary.lighten(0.15).toRgbString(),
        50: primary.lighten(0.3).toRgbString(),
        100: primary.toRgbString(),
        150: primary.darken(0.1).toRgbString(),
        200: primary.darken(0.2).toRgbString(),
        250: primary.darken(0.25).toRgbString(),
      },
    },
  };
};

type BrandingAssets = {
  logoUrl?: string;
  faviconUrl?: string;
  fullLogoUrl?: string;
  hasCustomBranding: boolean;
  clinicName?: string;
  isLoading: boolean;
};

const BrandingAssetsContext = createContext<BrandingAssets>({
  hasCustomBranding: false,
  isLoading: true,
});

/** Hook to access branding assets (logos, favicons) */
export const useBrandingAssets = (): BrandingAssets => {
  return useContext(BrandingAssetsContext);
};

export const BrandingProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const tenantId = useTenantId();
  const dispatch = useAppDispatch();

  const { useFeatureEntitlements, isLoading: isLoadingEntitlements } =
    useUserEntitlementsContext();
  const { isEntitled } = useFeatureEntitlements(FronteggFeatureKeys.Branding);

  useEffect(() => {
    if (tenantId) {
      void dispatch(initializeBranding(tenantId));
    }
  }, [dispatch, tenantId]);

  const { data, isLoading } = useGetClinicBrandingQuery(
    { clinicId: tenantId ?? '' },
    { skip: !tenantId },
  );

  const brandingValue = useMemo((): BrandingContextValue => {
    if (isLoading || isLoadingEntitlements) {
      return {
        overrideTheme: themeOverrideWhenCustom,
        overridePalette: brandingToPalette(paletteTokens.neutral.grey[150]),
      };
    }

    const customBranding = data?.Branding?.getClinicBranding;
    const hasCustomBranding =
      customBranding?.favicon ||
      customBranding?.logo ||
      customBranding?.primaryColor ||
      customBranding?.clinicName;

    if (!hasCustomBranding || !isEntitled) {
      return {
        overrideTheme: themeOverrideWhenCustom,
        overridePalette: undefined,
      };
    }

    return {
      overrideTheme: themeOverrideWhenCustom,
      overridePalette: brandingToPalette(customBranding?.primaryColor),
    };
  }, [isLoading, data, isEntitled, isLoadingEntitlements]);

  const brandingAssets = useMemo((): BrandingAssets => {
    if (isLoading || isLoadingEntitlements) {
      return {
        hasCustomBranding: false,
        isLoading: true,
      };
    }

    const customBranding = data?.Branding?.getClinicBranding;
    const hasCustomBranding =
      customBranding?.favicon ||
      customBranding?.logo ||
      customBranding?.primaryColor ||
      customBranding?.clinicName;

    if (!hasCustomBranding || !isEntitled) {
      return {
        logoUrl: '/v2/images/doxyLogoSmall.svg',
        faviconUrl: '/v2/images/faviconlogo.ico',
        fullLogoUrl: undefined,
        hasCustomBranding: false,
        clinicName: undefined,
        isLoading: false,
      };
    }

    return {
      logoUrl: customBranding?.favicon ?? undefined,
      faviconUrl: customBranding?.favicon ?? undefined,
      fullLogoUrl: customBranding?.logo ?? undefined,
      hasCustomBranding: true,
      clinicName: customBranding?.clinicName ?? undefined,
      isLoading: false,
    };
  }, [isLoading, data, isEntitled, isLoadingEntitlements]);

  return (
    <BrandingContext.Provider value={brandingValue}>
      <BrandingAssetsContext.Provider value={brandingAssets}>
        {children}
      </BrandingAssetsContext.Provider>
    </BrandingContext.Provider>
  );
};
